import {
  AWPTextTrackKind,
  CoreEvents,
  PlaybackState,
  ThumbnailVariants,
} from "@tv4/avod-web-player-common";
import type { Core } from "@tv4/avod-web-player-core";
import { isMobile } from "@tv4/avod-web-player-device-capabilities";
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AdBreakOverlay } from "./components/AdBreakOverlay/AdBreakOverlay";
import { AdShowingIndicator } from "./components/AdShowingIndicator/AdShowingIndicator";
import HtmlTextTracks from "./components/audio-text-tracks/HtmlTextTracks";
import { ChromeCastControls } from "./components/ChromeCastControls";
import ChromeCastOverlay from "./components/ChromeCastOverlay";
import { DebugOverlay } from "./components/DebugOverlay/DebugOverlay";
import { DefaultControls } from "./components/DefaultControls/DefaultControls";
import ErrorMessage from "./components/ErrorMessage/ErrorMessage";
import { Header } from "./components/Header";
import IconButton from "./components/IconButton";
import { InactivePrompt } from "./components/InactivePrompt/InactivePrompt";
import { MobileControls } from "./components/MobileControls/MobileControls";
import { PlaybackButton } from "./components/PlaybackButton";
import Poster from "./components/Poster";
import SkipMarkerButton from "./components/SkipMarkerButton";
import { Spinner } from "./components/spinner/Spinner";
import useAutoHideUI from "./hooks/useAutoHideUI";
import useCore from "./hooks/useCore";
import useSkinVisibilityChangeEvent from "./hooks/useSkinVisibilityChangeEvent";
import ChevronLeftSvg from "./icons/ChevronLeft.svg";
import { CoreProvider } from "./providers/CoreProvider";
import { LabelBoundingContainerProvider } from "./providers/LabelBoundingContainerProvider";
import {
  useLayoutSettings,
  useTracksMenuOpen,
  useUIVisibility,
} from "./providers/LayoutProvider";
import { useMetadata } from "./providers/MetadataProvider";
import { ThumbnailsProvider } from "./providers/ThumbnailsProvider";
import { useTranslations } from "./providers/TranslationsProvider";
import {
  FloatingBackButton,
  GradientMode,
  LabelBoundingContainer,
  SkinContainer,
  SkinWrapper,
} from "./styles";

export type TSkinProps = {
  core: Core;
  thumbnails?: ThumbnailVariants;
  forceShowSkin?: boolean;
  showInactivePrompt?: boolean;
  onExitClick?: () => void;
  onBackClick?: () => void;
  onCloseClick?: () => void;
  onVideoClick?: () => void;
  onPlayWhenEnded?: () => void;
};

const HIDE_SKIP_BUTTON_DELAY = 10 * 1000;
let skipButtonTimeout: number;

const PosterBackButton = ({ onBackClick }: { onBackClick: () => void }) => {
  const translations = useTranslations();
  return (
    <FloatingBackButton>
      <IconButton
        Icon={<ChevronLeftSvg />}
        label={translations("player__a11y__voice_over_back_button")}
        onClick={onBackClick}
        interactive
        hoverText={translations("player__a11y__hover_label_back_button")}
        forceLeft
        forceBottom
      />
    </FloatingBackButton>
  );
};

const Skin = forwardRef(
  (
    {
      core,
      thumbnails,
      forceShowSkin,
      showInactivePrompt,
      onBackClick,
      onCloseClick,
      onExitClick,
      onPlayWhenEnded,
    }: TSkinProps,
    skinWrapperRef: ForwardedRef<HTMLElement | null>
  ) => {
    const { poster } = useMetadata();
    const {
      playerState,
      controls,
      adMarkers,
      currentAdBreak,
      currentAd,
      chromeCastManager,
      options,
      error,
    } = useCore(core);

    const { uiVisible, setUIVisible } = useUIVisibility();
    const { noInitialLoadSpinner } = useLayoutSettings();
    const [isSkipButtonActive, setIsSkipButtonActive] = useState(false);
    const [buttonsEnabled, setButtonsEnabled] = useState(false);
    const { tracksMenuOpen } = useTracksMenuOpen();
    const labelBoundingContainerRef = useRef<HTMLDivElement>(null);

    const [started, setStarted] = useState(false);

    const {
      playbackState,
      isCasting,
      autoplayBlocked,
      activeTextTrack,
      isFullscreen,
      isPauseAd,
      debugOverlay,
      isLoaded,
    } = playerState;

    const idle = playbackState === PlaybackState.IDLE;
    const loading = playbackState === PlaybackState.LOADING;
    const seeking = playbackState === PlaybackState.SEEKING;
    const buffering = playbackState === PlaybackState.BUFFERING;
    const ended = playbackState === PlaybackState.ENDED;

    useEffect(() => {
      const handleToggleVote = () => {
        core.emitCoreEvent(CoreEvents.TOGGLE_VOTING, undefined);
      };

      document.addEventListener("toggleVote", handleToggleVote);

      return () => document.removeEventListener("toggleVote", handleToggleVote);
    }, [core]);

    useEffect(() => {
      const handleToggleChannelsOverlay = () => {
        core.emitCoreEvent(CoreEvents.TOGGLE_CHANNELS_OVERLAY, undefined);
      };

      document.addEventListener(
        "toggleChannelsOverlay",
        handleToggleChannelsOverlay
      );

      return () =>
        document.removeEventListener(
          "toggleChannelsOverlay",
          handleToggleChannelsOverlay
        );
    }, [core]);

    useEffect(() => {
      if (
        skinWrapperRef &&
        "current" in skinWrapperRef &&
        skinWrapperRef.current
      ) {
        const handleTouchStart = (e: TouchEvent) => {
          const container = skinWrapperRef.current;
          if (!container?.contains(e.target as Node)) {
            setUIVisible(false);
          }
        };

        document.addEventListener("touchstart", handleTouchStart);

        return () =>
          document.removeEventListener("touchstart", handleTouchStart);
      }
    }, [setUIVisible, skinWrapperRef]);

    const onInteractionStart = useCallback(() => {
      setIsSkipButtonActive(true);
      clearTimeout(skipButtonTimeout);
    }, [setIsSkipButtonActive]);

    const activeRef = useRef(false);

    const onInteractionEnd = useCallback(() => {
      skipButtonTimeout = window.setTimeout(
        () => setIsSkipButtonActive(false),
        HIDE_SKIP_BUTTON_DELAY
      );
      // using ref so timeout is not reset when active changes
      if (!activeRef.current) {
        // hide ui if not active
        setUIVisible(false);
      }
    }, [setIsSkipButtonActive, setUIVisible]);

    const { handleInteraction, interacting } = useAutoHideUI({
      onInteractionStart,
      onInteractionEnd,
    });

    const handleTouchUI = () => {
      // consider active when using touch
      activeRef.current = true;
      handleInteraction();
    };

    // clear timeout on unmount
    useEffect(() => () => clearTimeout(skipButtonTimeout), []);

    // mouse leave on computer will hide ui when moving mouse pointer away from player. pointerleave is not used because it is triggered by touch actions on touch devices.
    const handleMouseLeave = useCallback(() => {
      activeRef.current = false;
      // don't hide if still dragging
      if (!interacting()) {
        setUIVisible(false);
      }
    }, [setUIVisible, interacting]);

    const handlePointerOver = () => (activeRef.current = true);
    const handleBackClick = () => {
      if (isFullscreen) controls?.toggleFullscreen?.();
      onBackClick?.();
    };

    useEffect(() => {
      setTimeout(
        () => {
          setButtonsEnabled(uiVisible);
        },
        uiVisible ? 500 : 0
      );
    }, [uiVisible]);

    useEffect(() => {
      if (
        playbackState === PlaybackState.IDLE ||
        playbackState === PlaybackState.ENDED
      ) {
        setStarted(false);
      } else if (playbackState === PlaybackState.PLAYING) {
        setStarted(true);
      }
    }, [playbackState]);

    const hidePoster = isCasting || (started && !autoplayBlocked);

    const displayPosterContent = () => {
      if (hidePoster) return null;

      const hasError = error && Boolean(error);
      const isBlurred = Boolean(error);
      const isLoading = idle || loading;
      const canPlayback = !started || autoplayBlocked;

      return (
        <>
          <Poster poster={poster} blurred={isBlurred} />
          {hasError && <ErrorMessage error={error} onExitClick={onExitClick} />}
          {!hasError && !isLoading && canPlayback && (
            <PlaybackButton onPlayWhenEnded={onPlayWhenEnded} />
          )}
          {shouldShowBackButton && (
            <PosterBackButton onBackClick={handleBackClick} />
          )}
        </>
      );
    };

    const shouldShowSkin = useMemo(() => {
      if (forceShowSkin) return true;
      if (isCasting || tracksMenuOpen || playerState.preSeeking) return true;

      return started && !idle && !loading && uiVisible;
    }, [
      isCasting,
      forceShowSkin,
      started,
      idle,
      loading,
      uiVisible,
      tracksMenuOpen,
      playerState.preSeeking,
    ]);

    useSkinVisibilityChangeEvent(core, shouldShowSkin);

    const shouldShowBackButton = useMemo(() => {
      return onBackClick !== undefined || isFullscreen;
    }, [isFullscreen, onBackClick]);

    const emitAdClickEventAndPauseIfAllowed = (url: string) => {
      core.emitCoreEvent(CoreEvents.AD_CLICK, {
        url: url,
      });
      if (playerState.canPause) {
        controls?.pause?.({ programmatic: true });
      }
    };

    const emitInactiveAnswer = (answer: boolean) => {
      if (answer) {
        core.emitCoreEvent(CoreEvents.USER_ACTIVE_CONFIRM, undefined);
      } else {
        core.emitCoreEvent(CoreEvents.USER_ACTIVE_DECLINE, undefined);
      }
    };

    const getCurrentCoreState = () => {
      return core.getState();
    };

    const getDebugInfo = () => {
      return core.getDebugInfo();
    };

    const showTextTracks =
      activeTextTrack?.kind !== AWPTextTrackKind.NOTHING &&
      !playerState.isPauseAd &&
      !playerState.isAd;

    let Controls = DefaultControls;

    const translations = useTranslations();

    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        // If we are in fullscreen, use Tab to immediately render UI
        // instead of first requiring focusing on the Focus Wrapper
        if (isFullscreen && e.key === "Tab") {
          handleInteraction();
          activeRef.current = true;
        }
      };

      window.addEventListener("keydown", onKeyDown);
      return () => {
        window.removeEventListener("keydown", onKeyDown);
      };
    }, [isFullscreen, handleInteraction]);

    if (isCasting) {
      Controls = ChromeCastControls;
    } else if (isMobile()) {
      Controls = MobileControls;
    }

    return (
      <CoreProvider
        playerState={playerState}
        controls={controls}
        adMarkers={adMarkers}
        currentAdBreak={currentAdBreak}
        currentAd={currentAd}
        options={options}
        chromeCastManager={chromeCastManager}
        getCurrentState={getCurrentCoreState}
        getDebugInfo={getDebugInfo}
      >
        <SkinWrapper
          ref={skinWrapperRef}
          onClick={() => {
            handleInteraction();
            activeRef.current = true;
          }}
          // EU Accessibility Directive
          onKeyDown={() => {
            handleInteraction();
            activeRef.current = true;
          }}
          onKeyUp={() => {
            handleInteraction();
            activeRef.current = true;
          }}
          onFocus={() => {
            // When focused, we want to show the UI
            // and consider it active.
            handleInteraction();
            activeRef.current = true;
          }}
          onPointerMove={handleInteraction}
          onTouchStart={handleTouchUI}
          onPointerOver={handlePointerOver}
          onMouseLeave={handleMouseLeave}
          $isActive={uiVisible}
          aria-label={translations("player__a11y__voice_over_video_controls")}
        >
          <LabelBoundingContainer ref={labelBoundingContainerRef}>
            {isCasting && <ChromeCastOverlay />}

            {!ended && isSkipButtonActive && (
              <SkipMarkerButton isSkipButtonActive={isSkipButtonActive} />
            )}

            {hidePoster && isLoaded && !ended && (
              <SkinContainer
                id="skin-container"
                $visible={shouldShowSkin}
                $gradientMode={
                  isPauseAd ? GradientMode.BOTTOM : GradientMode.BOTH
                }
                $buttonsEnabled={buttonsEnabled}
              >
                {
                  <LabelBoundingContainerProvider
                    containerRef={labelBoundingContainerRef}
                  >
                    <Header
                      onBackClick={onBackClick}
                      onCloseClick={onCloseClick}
                    />
                    <ThumbnailsProvider thumbnails={thumbnails} useImageProxy>
                      <Controls />
                    </ThumbnailsProvider>
                  </LabelBoundingContainerProvider>
                }
              </SkinContainer>
            )}

            {showInactivePrompt && (
              <InactivePrompt onAnswer={emitInactiveAnswer} />
            )}

            {displayPosterContent()}

            {((!noInitialLoadSpinner && loading) || buffering || seeking) && (
              <Spinner />
            )}

            {
              <AdBreakOverlay
                visible={!autoplayBlocked && shouldShowSkin}
                makeSpaceForBackButton={shouldShowBackButton}
                onAdButtonClick={emitAdClickEventAndPauseIfAllowed}
              />
            }

            {
              // Never de-render this, it is grabbed by id
              // in PauseAdEngine.ts (╯°□°）╯︵ ┻━┻
              <AdShowingIndicator
                visible={isPauseAd}
                maximized={shouldShowSkin}
              />
            }
          </LabelBoundingContainer>
        </SkinWrapper>

        {showTextTracks && <HtmlTextTracks isRaised={shouldShowSkin} />}

        {debugOverlay && <DebugOverlay />}
      </CoreProvider>
    );
  }
);

export default Skin;
